<template>
  <Dialog
    visible
    header="Copy classes to segments"
    modal
    :style="{ width: '50vw', minHeight: '30vw', maxHeight: '90vw' }"
    :closable="false"
    :dismissable-mask="false"
    block-scroll
    :draggable="false"
  >
    <template #default>
      <VdForm
        class="copy-classes-form-page"
        :form="form"
        :form-changed="formChanged"
        :allow-delete="false"
        title="Copy classes"
        save-button-label="Apply"
        :save-loading="saveLoading"
        :disable-save-button="loading || form.selectedClasses.length === 0 || form.selectedSegments.length === 0"
        :back-link="backLink"
        :loading="loading"
        @submit="saveForm"
        @cancel="onDialogClose"
      >
        <div class="copy-section">
          <div class="copy-section-header">
            <h3>Classes to copy</h3>
            <div v-if="!loading && sourceClasses.length > 0" class="select-actions">
              <button type="button" @click="selectAllClasses">Select all</button>
              <button type="button" @click="selectNoneClasses">Select none</button>
            </div>
          </div>
          <div class="hint">
            <Skeleton v-if="loading" height="1.5rem" width="200px" style="margin-bottom: 0.5rem" />
            <span v-else>
              Copying from segment:
              <strong>{{ sourceSegmentName }}</strong>
            </span>
          </div>
          <template v-if="loading">
            <Skeleton v-for="i in 3" :key="i" height="1.5rem" width="200px" style="margin-bottom: 0.5rem" />
          </template>
          <template v-else>
            <p v-if="sourceClasses.length === 0" class="hint">No classes configured for this segment.</p>
            <div v-for="cls in sourceClasses" :key="cls.technicalName" class="checkbox-row">
              <Checkbox
                v-model="form.selectedClasses"
                :value="cls.technicalName"
                :input-id="`class-${cls.technicalName}`"
              />
              <label :for="`class-${cls.technicalName}`">{{ cls.displayName }}</label>
            </div>
          </template>
        </div>

        <div class="copy-section">
          <div class="copy-section-header">
            <h3>Mode</h3>
          </div>

          <SelectButton
            v-model="copyMode"
            :options="copyModeOptions"
            option-label="label"
            option-value="value"
            size="small"
            class="copy-mode"
          />
          <div class="hint">
            <template v-if="copyMode === 'add'">
              Selected classes are added or updated; other classes in the target are kept.
            </template>
            <template v-else>All classes in the target are replaced with the selected ones.</template>
          </div>
        </div>

        <div class="copy-section">
          <div class="copy-section-header">
            <h3>Target segments</h3>
            <div v-if="!loading && targetSegments.length > 0" class="select-actions">
              <button type="button" @click="selectAllSegments">Select all</button>
              <button type="button" @click="selectNoneSegments">Select none</button>
            </div>
          </div>
          <template v-if="loading">
            <Skeleton v-for="i in 3" :key="i" height="1.5rem" width="150px" style="margin-bottom: 0.5rem" />
          </template>
          <template v-else>
            <div v-for="seg in targetSegments" :key="seg.id" class="checkbox-row">
              <Checkbox v-model="form.selectedSegments" :value="seg.id" :input-id="`segment-${seg.id}`" />
              <label :for="`segment-${seg.id}`">{{ seg.name }}</label>
            </div>
          </template>
        </div>
      </VdForm>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { RouteLocationRaw, useRoute, useRouter } from "vue-router";
import { cloneDeep } from "lodash";
import Dialog from "primevue/dialog";
import Checkbox from "primevue/checkbox";
import SelectButton from "primevue/selectbutton";
import Skeleton from "primevue/skeleton";
import { VdForm } from "@voodoo.io/voodoo-ui";

import {
  AbtestType,
  CohortInput,
  CohortTreatment,
  useCohortsListPageQuery,
  useCohortFormPageSaveMutation,
  useMutationResponse,
} from "@/graphql";
import links from "@/router/links";
import { mapCohortUpdatePayload } from "@/utils/mapCohortUpdatePayload";

const router = useRouter();
const route = useRoute();
const { gameId, abtestId, cohortId, segmentId, versionId } = route.query as {
  gameId: string;
  abtestId: string;
  cohortId: string;
  segmentId: string;
  versionId: string;
};

const { result, loading } = useCohortsListPageQuery({ gameId, versionId, abtestId }, { fetchPolicy: "cache-first" });

const abtest = computed(() => result.value?.game?.version?.currentAbtest);
const cohort = computed(() => abtest.value?.cohorts.find(({ id }) => id === cohortId));

const sourceClasses = computed(() =>
  (cohort.value?.treatments?.find((t) => t.segmentId === segmentId)?.classesOverrides ?? [])
    .filter((cls) => cls.class.technicalName !== "VT.ShopManagementClass")
    .map((cls) => ({ technicalName: cls.class.technicalName, displayName: cls.class.displayName }))
);

const sourceSegmentName = computed(
  () => abtest.value?.dynamicSegments.find((seg) => seg.id === segmentId)?.name ?? segmentId
);

const targetSegments = computed(() => (abtest.value?.dynamicSegments ?? []).filter((seg) => seg.id !== segmentId));

type CopyMode = "add" | "replace";

const copyMode = ref<CopyMode>("add");
const copyModeOptions: { label: string; value: CopyMode }[] = [
  { label: "Add", value: "add" },
  { label: "Replace", value: "replace" },
];

const form = reactive({
  selectedClasses: [] as string[],
  selectedSegments: [] as string[],
});

const initialized = ref(false);
watch(
  cohort,
  (c) => {
    if (c && !initialized.value) {
      form.selectedClasses = sourceClasses.value.map((cls) => cls.technicalName);
      form.selectedSegments = targetSegments.value.map((seg) => seg.id);
      initialized.value = true;
    }
  },
  { immediate: true }
);

const formChanged = computed(() => form.selectedClasses.length > 0 && form.selectedSegments.length > 0);

const selectAllClasses = () => {
  form.selectedClasses = sourceClasses.value.map((cls) => cls.technicalName);
};
const selectNoneClasses = () => {
  form.selectedClasses = [];
};
const selectAllSegments = () => {
  form.selectedSegments = targetSegments.value.map((seg) => seg.id);
};
const selectNoneSegments = () => {
  form.selectedSegments = [];
};

const backLink: RouteLocationRaw = {
  name: links.games.abtests.dynamicTreatment,
  params: {
    gameId,
    abtestId,
    forceReload: "true",
    routingCohortId: cohortId,
    routingSegmentId: segmentId,
  },
};

const onDialogClose = () => {
  router.push(backLink);
};

const { mutate: save, loading: saveLoading } = useCohortFormPageSaveMutation({});

const saveForm = async () => {
  if (!cohort.value) {
    return;
  }

  const input: CohortInput = {
    name: cohort.value.name,
    description: cohort.value.description,
    allocationRate: cohort.value.allocationRate,
    isDynamic: true,
    treatments: [],
  };

  const allTreatments =
    mapCohortUpdatePayload(cloneDeep(cohort.value.treatments) as unknown as CohortTreatment[]) ?? [];

  const sourceInputTreatment = allTreatments.find((t) => t.segmentId === segmentId);
  const classesToCopy = (sourceInputTreatment?.classesOverrides ?? []).filter((cls) =>
    form.selectedClasses.includes(cls.classId)
  );

  const updatedTreatments = allTreatments.map((treatment) => {
    if (!form.selectedSegments.includes(treatment.segmentId)) {
      return treatment;
    }

    const shopOverrides = (treatment.classesOverrides ?? []).filter((cls) => cls.classId === "VT.ShopManagementClass");
    const keptOverrides =
      copyMode.value === "add"
        ? (treatment.classesOverrides ?? []).filter(
            (cls) => cls.classId !== "VT.ShopManagementClass" && !form.selectedClasses.includes(cls.classId)
          )
        : [];
    const newClassOverrides = [...shopOverrides, ...keptOverrides, ...classesToCopy];

    const treatmentTypes = [...treatment.treatmentTypes];
    if (
      newClassOverrides.some((cls) => cls.classId !== "VT.ShopManagementClass") &&
      !treatmentTypes.includes(AbtestType.Class)
    ) {
      treatmentTypes.push(AbtestType.Class);
    }

    return { ...treatment, classesOverrides: newClassOverrides, treatmentTypes };
  });

  if (classesToCopy.length > 0) {
    for (const seg of form.selectedSegments) {
      if (!updatedTreatments.find((t) => t.segmentId === seg)) {
        updatedTreatments.push({
          segmentId: seg,
          id: "",
          rank: updatedTreatments.length + 1,
          treatmentTypes: [AbtestType.Class],
          classesOverrides: classesToCopy,
        });
      }
    }
  }

  input.treatments = updatedTreatments;

  const response = await save({ gameId, versionId, abtestId, cohortId, input });
  const data = useMutationResponse(response, (resp) => resp?.saveCohort);
  if (data?.success) {
    router.push(backLink);
  }
};
</script>

<style lang="scss">
.copy-classes-form-page {
  .copy-mode {
    margin-bottom: 1rem;
  }

  .hint {
    font-size: 13px;
    color: var(--vd-color-text-light-2);
    margin-bottom: 1.5rem;
  }

  .copy-section {
    margin-bottom: 1.5rem;

    .copy-section-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0.75rem;

      h3 {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
      }

      .select-actions {
        display: flex;
        gap: 0.5rem;

        button {
          background: none;
          border: none;
          padding: 0;
          font-size: 12px;
          color: var(--vd-color-blue-default);
          cursor: pointer;

          &:hover {
            text-decoration: underline;
          }
        }
      }
    }

    .checkbox-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.5rem;

      label {
        cursor: pointer;
      }
    }
  }
}
</style>
